// 2026-09-23 - FormuLearning - AI 해설 본문 경량 마크다운 표시 (이슈 #67 S4-7)
// 파일 위치: frontend/src/components/feedback/MarkdownText.tsx
// 목적: Step 4 AI 해설(`round.ai_summary_ko`·`experiment_batch.ai_commentary_ko`)은 Gemini 가 쓴 마크다운이다 —
//       목록(`-`·`*`, 들여쓰기 2단) · `**굵게**` · `*기울임*` · `` `코드` `` · `#` 제목, 가끔 `$\ge$` 같은 LaTeX 기호.
//       이 범위만 읽어 **React 텍스트 노드로만** 만든다(HTML 주입 없음). 외부 렌더러를 두지 않은 근거는
//       작업계획 D10. 모르는 표기는 원문 그대로 남는다 — 지우지 않는다.

import type { ReactNode } from 'react'

/** 해설에 나오는 LaTeX 기호 — `$\ge$` → `≥`. 표에 없는 명령은 `$` 만 걷고 원문을 둔다. */
const LATEX: Record<string, string> = {
  ge: '≥', geq: '≥', le: '≤', leq: '≤', pm: '±', times: '×', approx: '≈', sim: '~',
  to: '→', rightarrow: '→', leftarrow: '←', uparrow: '↑', downarrow: '↓', neq: '≠', ne: '≠',
  sigma: 'σ', mu: 'μ', Delta: 'Δ', delta: 'δ', alpha: 'α', beta: 'β', circ: '°', cdot: '·',
}

function latex(text: string): string {
  return text.replace(/\$([^$\n]{1,40})\$/g, (_, body: string) =>
    body.replace(/\\([A-Za-z]+)/g, (m: string, name: string) => LATEX[name] ?? m).replace(/[{}]/g, '').trim())
}

/** 한 줄 안의 강조 — `**굵게**` · `*기울임*` · `` `코드` ``. `_` 기울임은 읽지 않는다(성분명·변수명의 밑줄을 깨뜨린다). */
function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\s][^*]*\*)/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    const tok = m[0]
    const k = `${key}-${i++}`
    if (tok.startsWith('**')) out.push(<strong key={k} className="font-semibold text-text-body">{tok.slice(2, -2)}</strong>)
    else if (tok.startsWith('`')) out.push(<code key={k} className="rounded px-0.5 text-[0.95em]" style={{ background: 'var(--bg-chip-purple)' }}>{tok.slice(1, -1)}</code>)
    else out.push(<em key={k}>{tok.slice(1, -1)}</em>)
    last = m.index + tok.length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

type Block =
  | { kind: 'heading'; text: string }
  | { kind: 'para'; text: string }
  | { kind: 'item'; level: number; text: string }

function parse(src: string): Block[] {
  const blocks: Block[] = []
  for (const raw of latex(src).replace(/\r\n?/g, '\n').split('\n')) {
    if (raw.trim() === '' || /^(?:-{3,}|\*{3,}|_{3,})$/.test(raw.trim())) continue   // 빈 줄 · 구분선(`---`)은 버린다
    const indent = raw.length - raw.trimStart().length
    const line = raw.trim()
    const h = /^#{1,6}\s+(.*)$/.exec(line)
    const li = /^(?:[-*+•]|\d+[.)])\s+(.*)$/.exec(line)
    if (h) blocks.push({ kind: 'heading', text: h[1] })
    else if (li) blocks.push({ kind: 'item', level: indent >= 2 ? 1 : 0, text: li[1] })
    else {
      const prev = blocks[blocks.length - 1]
      // 목록 항목 아래 들여쓴 본문 줄은 그 항목에 잇는다(해설의 «- **제목**\n  본문» 형태)
      if (prev && prev.kind === 'item' && indent > 0) prev.text += '\n' + line
      else blocks.push({ kind: 'para', text: line })
    }
  }
  return blocks
}

function lines(text: string, key: string): ReactNode[] {
  return text.split('\n').flatMap((part, i) => (i === 0 ? inline(part, `${key}-${i}`) : [<br key={`${key}-br${i}`} />, ...inline(part, `${key}-${i}`)]))
}

export default function MarkdownText({ text, className = '' }: { text: string; className?: string }) {
  const blocks = parse(text)
  return (
    <div className={`space-y-1 text-[11px] leading-relaxed text-text-body ${className}`} data-testid="markdown-text">
      {blocks.map((b, i) => {
        const k = `b${i}`
        if (b.kind === 'heading') return <p key={k} className="font-semibold">{inline(b.text, k)}</p>
        if (b.kind === 'para') return <p key={k}>{inline(b.text, k)}</p>
        return (
          <div key={k} className="flex gap-1.5" style={{ paddingLeft: b.level * 12 }}>
            <span className="text-text-muted">{b.level === 0 ? '•' : '–'}</span>
            <span className="min-w-0">{lines(b.text, k)}</span>
          </div>
        )
      })}
    </div>
  )
}
